import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../components/mainLayout";
import { api } from "../api";
import { showError, showSuccess } from "../utils/toast";
import "../sass/pages/servicosAdicionais.scss";

const EMPTY_FORM = { Codigo: "", Descricao: "", Valor: "" };

const normalizarTexto = (valor) => String(valor ?? "").trim();

const parseValorMonetario = (valor) => {
  if (valor == null) return null;

  let texto = String(valor).trim();

  if (!texto) return null;

  texto = texto.replace(/[^\d.,-]/g, "");

  if (!texto) return null;

  const temVirgula = texto.includes(",");
  const temPonto = texto.includes(".");

  if (temVirgula && temPonto) {
    if (texto.lastIndexOf(",") > texto.lastIndexOf(".")) {
      // 1.240,50
      texto = texto.replace(/\./g, "").replace(",", ".");
    } else {
      // 1,240.50
      texto = texto.replace(/,/g, "");
    }
  } else if (temVirgula) {
    // 240,50
    texto = texto.replace(",", ".");
  }
  // Somente ponto: 240.50 -> mantém como decimal.

  const numero = Number(texto);

  return Number.isFinite(numero) ? numero : null;
};

const valorParaNumero = (valor) => {
  const numero = parseValorMonetario(valor);

  return numero == null ? 0 : numero;
};

const formatarValor = (valor) => {
  const numero = parseValorMonetario(valor);

  if (numero == null) return normalizarTexto(valor);

  return numero.toFixed(2).replace(".", ",");
};

const normalizarValorInput = (valor) => {
  let texto = String(valor ?? "");

  texto = texto.replace(/[^\d,.]/g, "");

  const temVirgula = texto.includes(",");
  const temPonto = texto.includes(".");

  if (temVirgula && temPonto) {
    texto = texto.replace(/\./g, "");
  }

  texto = texto.replace(".", ",");

  const partes = texto.split(",");

  if (partes.length > 1) {
    texto = `${partes[0]},${partes.slice(1).join("").slice(0, 2)}`;
  }

  return texto;
};

const ServicosAdicionais = ({ somenteConsulta = false }) => {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const carregarLista = async (filtro = "") => {
    try {
      setLoading(true);

      const params = filtro ? { search: filtro } : undefined;
      const { data } = await api.get("/ssw/servicos", { params });

      setLista(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("[Servicos] listar erro:", err);
      showError("Falha ao carregar serviços adicionais.");
      setLista([]);
    } finally {
      setLoading(false);
    }
  };

  const limparForm = () => {
    setForm(EMPTY_FORM);
    setSelectedId(null);
    setIsEditMode(false);
  };

  const abrirModalNovo = () => {
    if (somenteConsulta) return;

    limparForm();
    setModalOpen(true);
  };

  const abrirModalEditar = (item) => {
    if (somenteConsulta) return;

    setSelectedId(item.ID);
    setForm({
      Codigo: item.Codigo ?? "",
      Descricao: item.Descricao ?? "",
      Valor: formatarValor(item.Valor),
    });
    setIsEditMode(true);
    setModalOpen(true);
  };

  const fecharModal = () => {
    if (saving || deleting) return;
    setModalOpen(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: name === "Valor" ? normalizarValorInput(value) : value,
    }));
  };

  const handleValorBlur = () => {
    if (!form.Valor) return;

    setForm((prev) => ({ ...prev, Valor: formatarValor(prev.Valor) }));
  };

  const validarForm = () => {
    const descricao = normalizarTexto(form.Descricao);
    const valor = normalizarTexto(form.Valor);

    if (!descricao) {
      showError("Descrição é obrigatória.");
      return false;
    }

    if (!valor) {
      showError("Valor é obrigatório.");
      return false;
    }

    if (valorParaNumero(valor) <= 0) {
      showError("Valor inválido.");
      return false;
    }

    if (isEditMode && selectedId) {
      const codigo = normalizarTexto(form.Codigo);

      if (!codigo) {
        showError("Código não encontrado para este serviço.");
        return false;
      }

      if (!/^\d+$/.test(codigo)) {
        showError("Código inválido no registro.");
        return false;
      }
    }

    return true;
  };

  const handleSalvar = async () => {
    if (somenteConsulta) return;
    if (!validarForm()) return;

    const isEdit = isEditMode && selectedId;

    const payload = {
      Descricao: normalizarTexto(form.Descricao),
      Valor: formatarValor(form.Valor),
    };

    if (isEdit) {
      payload.Codigo = normalizarTexto(form.Codigo);
    }

    try {
      setSaving(true);

      const response = isEdit
        ? await api.put(`/ssw/servicos/${selectedId}`, payload)
        : await api.post("/ssw/servicos", payload);

      showSuccess(
        isEdit
          ? "Serviço atualizado com sucesso."
          : "Serviço criado com sucesso.",
      );

      const salvo = response?.data;

      await carregarLista(search);

      if (salvo?.ID) {
        setSelectedId(salvo.ID);
        setIsEditMode(true);
        setForm({
          Codigo: salvo.Codigo ?? "",
          Descricao: salvo.Descricao ?? payload.Descricao,
          Valor: formatarValor(salvo.Valor ?? payload.Valor),
        });
      }

      setModalOpen(false);
    } catch (err) {
      console.error("[Servicos] salvar erro:", err);

      showError(
        err?.response?.data?.error ||
          err?.response?.data?.erro ||
          err?.response?.data?.message ||
          "Falha ao salvar serviço.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleExcluir = async () => {
    if (somenteConsulta) return;

    if (!isEditMode || !selectedId) {
      showError("Selecione um serviço para excluir.");
      return;
    }

    const ok = window.confirm(
      "Confirma a exclusão deste serviço? Clientes que já possuem este plano vinculado não serão alterados.",
    );

    if (!ok) return;

    try {
      setDeleting(true);

      await api.delete(`/ssw/servicos/${selectedId}`);

      showSuccess("Serviço excluído com sucesso.");

      limparForm();
      setModalOpen(false);

      await carregarLista(search);
    } catch (err) {
      console.error("[Servicos] excluir erro:", err);

      showError(
        err?.response?.data?.error ||
          err?.response?.data?.erro ||
          err?.response?.data?.message ||
          "Falha ao excluir serviço.",
      );
    } finally {
      setDeleting(false);
    }
  };

  const totalServicos = lista.length;

  const valorMedio = useMemo(() => {
    if (!lista.length) return "0,00";

    const total = lista.reduce(
      (acc, item) => acc + valorParaNumero(item.Valor),
      0,
    );

    return (total / lista.length).toFixed(2).replace(".", ",");
  }, [lista]);

  const maiorValor = useMemo(() => {
    if (!lista.length) return "0,00";

    const maior = lista.reduce(
      (acc, item) => Math.max(acc, valorParaNumero(item.Valor)),
      0,
    );

    return maior.toFixed(2).replace(".", ",");
  }, [lista]);

  useEffect(() => {
    carregarLista();
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      carregarLista(search);
    }, 300);

    return () => clearTimeout(handle);
  }, [search]);

  return (
    <MainLayout>
      <div className="servicos-page">
        <header className="servicos-header">
          <div>
            <span className="servicos-kicker">Comercial</span>
            <h1>Serviços Adicionais</h1>
            <p className="servicos-subtitle">
              Cadastre os serviços extras que poderão ser vinculados aos clientes.
            </p>
          </div>

          {!somenteConsulta && (
            <button
              type="button"
              className="btn btn-primary servicos-header-action"
              onClick={abrirModalNovo}
            >
              Novo serviço
            </button>
          )}
        </header>

        <section className="servicos-summary" aria-label="Resumo dos serviços">
          <article>
            <span>Total cadastrado</span>
            <strong>{totalServicos}</strong>
          </article>

          <article>
            <span>Valor médio</span>
            <strong>R$ {valorMedio}</strong>
          </article>

          <article>
            <span>Maior valor</span>
            <strong>R$ {maiorValor}</strong>
          </article>
        </section>

        <section className="servicos-toolbar">
          <div className="servicos-toolbar-left">
            <label htmlFor="search-servicos">Buscar serviço</label>
            <input
              id="search-servicos"
              type="text"
              placeholder="Filtrar por código ou descrição..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <div className="servicos-toolbar-right">
            <span className="servicos-status">
              {loading
                ? "Carregando..."
                : lista.length === 1
                  ? "1 serviço encontrado"
                  : `${lista.length} serviços encontrados`}
            </span>

            {!somenteConsulta && (
              <button
                type="button"
                className="btn btn-primary servicos-toolbar-action"
                onClick={abrirModalNovo}
              >
                Novo serviço
              </button>
            )}
          </div>
        </section>

        <section className="servicos-grid" aria-label="Serviços adicionais">
          <div className="servicos-table">
            <table>
              <thead>
                <tr>
                  <th style={{ width: "90px" }}>Código</th>
                  <th>Descrição</th>
                  <th style={{ width: "140px" }}>Valor</th>
                  {!somenteConsulta && <th style={{ width: "110px" }}>Ação</th>}
                </tr>
              </thead>

              <tbody>
                {lista.length === 0 && !loading && (
                  <tr>
                    <td
                      colSpan={somenteConsulta ? 3 : 4}
                      className="servicos-grid-empty"
                    >
                      Nenhum serviço encontrado.
                    </td>
                  </tr>
                )}

                {lista.map((item) => (
                  <tr
                    key={item.ID}
                    className={
                      item.ID === selectedId
                        ? "servicos-row selected"
                        : "servicos-row"
                    }
                    onClick={() => abrirModalEditar(item)}
                  >
                    <td className="col-codigo">#{item.Codigo}</td>
                    <td className="col-descricao">{item.Descricao}</td>
                    <td className="col-valor">R$ {formatarValor(item.Valor)}</td>

                    {!somenteConsulta && (
                      <td className="col-acao">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            abrirModalEditar(item);
                          }}
                        >
                          Editar
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="servicos-mobile-list">
            {lista.length === 0 && !loading && (
              <div className="servicos-mobile-empty">
                Nenhum serviço encontrado.
              </div>
            )}

            {lista.map((item) => (
              <article
                key={item.ID}
                className={`servico-card ${
                  item.ID === selectedId ? "selected" : ""
                }`}
                onClick={() => abrirModalEditar(item)}
              >
                <div className="servico-card__header">
                  <div>
                    <span>#{item.Codigo}</span>
                    <h3>{item.Descricao}</h3>
                  </div>

                  <strong>R$ {formatarValor(item.Valor)}</strong>
                </div>

                {!somenteConsulta && (
                  <div className="servico-card__actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={(event) => {
                        event.stopPropagation();
                        abrirModalEditar(item);
                      }}
                    >
                      Editar serviço
                    </button>
                  </div>
                )}
              </article>
            ))}
          </div>
        </section>

        {modalOpen && (
          <div className="servicos-modal-backdrop" onClick={fecharModal}>
            <div
              className="servicos-modal"
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <header className="servicos-modal-header">
                <div>
                  <span className="servicos-modal-kicker">
                    {isEditMode ? "Edição" : "Novo cadastro"}
                  </span>
                  <h2>{isEditMode ? "Editar serviço" : "Novo serviço"}</h2>
                  <p>
                    Este serviço ficará disponível para vínculo no cadastro do
                    cliente.
                  </p>
                </div>

                <button
                  type="button"
                  className="modal-close"
                  onClick={fecharModal}
                  aria-label="Fechar"
                  disabled={saving || deleting}
                >
                  ×
                </button>
              </header>

              <section className="servicos-modal-body">
                {isEditMode && (
                  <div className="form-group">
                    <label>Código</label>
                    <div className="code-pill">#{form.Codigo || "-"}</div>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="Descricao">Descrição do serviço</label>
                  <input
                    id="Descricao"
                    name="Descricao"
                    type="text"
                    value={form.Descricao}
                    onChange={handleChange}
                    disabled={somenteConsulta || saving || deleting}
                    placeholder="Ex.: Backup em nuvem, módulo fiscal, suporte premium..."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="Valor">Valor padrão</label>
                  <input
                    id="Valor"
                    name="Valor"
                    type="text"
                    inputMode="decimal"
                    value={form.Valor}
                    onChange={handleChange}
                    onBlur={handleValorBlur}
                    disabled={somenteConsulta || saving || deleting}
                    placeholder="0,00"
                  />
                  <small>
                    Este valor será sugerido ao vincular o serviço no cliente.
                  </small>
                </div>
              </section>

              {!somenteConsulta && (
                <footer className="servicos-modal-actions">
                  {isEditMode && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleExcluir}
                      disabled={deleting || saving}
                    >
                      {deleting ? "Excluindo..." : "Excluir"}
                    </button>
                  )}

                  <div className="spacer" />

                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={fecharModal}
                    disabled={saving || deleting}
                  >
                    Cancelar
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSalvar}
                    disabled={saving || deleting}
                  >
                    {saving ? "Salvando..." : "Salvar"}
                  </button>
                </footer>
              )}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ServicosAdicionais;
